import React, { useCallback, useEffect, useState } from "react";
import { Form, Input, message, Modal } from "antd";
import I18n from "../../../../../../i18n";
import {
  Loading,
  UserFailure,
  UserRepository,
  validateEmail,
} from "../../../../../../../domain";
import { ServiceLocater, Services } from "../../../../../../../service-locater";
import { loadUser } from "../../../../../../../application/user-slice";
import { useAppDispatch } from "../../../../../../../application/store";

const { Item, useForm } = Form;

type Props = { visible: boolean; onClose: () => void; currentEmail?: string };

const EditEmailModal: React.FC<Props> = ({
  visible,
  onClose,
  currentEmail,
}) => {
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState<Loading>("idle");
  const [failure, setFailure] = useState<UserFailure | undefined>(undefined);

  const stableSetFields = useCallback(form.setFields, []);
  useEffect(() => {
    stableSetFields([{ name: "email", value: currentEmail }]);
    setIsEmailValid(
      validateEmail(currentEmail === undefined ? "" : currentEmail)
    );
  }, [stableSetFields, currentEmail]);

  const submit = async () => {
    if (isEmailValid) {
      setLoading("pending");
      const res = await ServiceLocater.get<UserRepository>(
        Services.UserRepository
      ).edit({ email: form.getFieldValue("email") });
      res.fold(
        (l) => {
          setLoading("failed");
          l.maybeMap({
            emailAlreadyUsedFailure: () => {
              setFailure(l);
            },
            orElse: () => {
              message.error(I18n.general.networkError);
            },
          });
        },
        (_) => {
          setLoading("succeeded");
          dispatch(loadUser());
          onClose();
        }
      );
    } else {
      setHasSubmitted(true);
    }
  };

  const getEmailErrorText = (): string | undefined => {
    if (!hasSubmitted) return undefined;
    if (isEmailValid !== undefined && !isEmailValid)
      return I18n.general.emailInvalidError;
    if (failure)
      return failure?.maybeMap({
        emailAlreadyUsedFailure: () => I18n.general.emailAlreadyUsedError,
        orElse: () => undefined,
      });
    return undefined;
  };

  const getEmailValidationStatus = (): "error" | undefined => {
    if (!hasSubmitted) return undefined;
    if (isEmailValid !== undefined && !isEmailValid) return "error";
    if (failure) {
      failure?.maybeMap<"error" | undefined>({
        emailAlreadyUsedFailure: () => "error",
        orElse: () => undefined,
      });
    }
    return undefined;
  };

  const isLoading = loading === "pending";
  return (
    <Modal
      title={I18n.profile.editProfile.editEmail}
      visible={visible}
      closable={!isLoading}
      maskClosable={!isLoading}
      onCancel={
        isLoading
          ? undefined
          : () => {
              setHasSubmitted(false);
              form.setFields([{ name: "email", value: currentEmail }]);
              onClose();
            }
      }
      onOk={submit}
      confirmLoading={isLoading}
      width={335}
      bodyStyle={{ paddingBottom: 0 }}
    >
      <Form form={form}>
        <Item
          name="email"
          help={getEmailErrorText()}
          validateStatus={getEmailValidationStatus()}
        >
          <Input
            disabled={isLoading}
            placeholder={I18n.general.email}
            spellCheck={false}
            type="email"
            onChange={(value) => {
              setFailure(undefined);
              setIsEmailValid(validateEmail(value.target.value));
              form.setFields([{ name: "email", value: value.target.value }]);
            }}
          />
        </Item>
      </Form>
    </Modal>
  );
};

export default EditEmailModal;
